import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone, Mail, Sparkles, Gift } from "lucide-react";
import SectionHeading from "../components/ui/SectionHeading.jsx";
import CTABanner from "../components/ui/CTABanner.jsx";
import { Reveal, MaskLines, ImageReveal } from "../components/motion/Reveal.jsx";
import useSeo from "../hooks/useSeo.js";
import { CLUBS, VARACHHA_EXPERIENCE, MEMBERSHIPS } from "../content.js";

export default function ClubVarachha() {
  useSeo({
    title: "Oneiros Varachha | A New Chapter for Oneiros in Surat — Now Open",
    description:
      "Oneiros Varachha is now open — premium gym, Sky Deck Run, swimming, cricket arena, padel-style sports courts, meditation & recovery suites, kids zones and the Blue Moon launch offer: first 500 members get NO AMC.",
    image: "/oneiros/varachha-outer.png",
  });
  return (
    <>
      <section data-testid="varachha-hero" className="mx-auto max-w-[1440px] px-6 pb-14 pt-36 lg:px-12 lg:pt-48">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <SectionHeading
              eyebrow="Oneiros Varachha — Now Open"
              lines={["A New Chapter.", "A New Destination."]}
              copy="A new Oneiros destination bringing the brand's philosophy of fitness, sports, family and community closer to Varachha — with something Surat has been missing."
            />
            <Reveal delay={0.2} className="mt-10 flex flex-col gap-4 sm:flex-row">
              <Link
                data-testid="varachha-explore-btn"
                to="/contact"
                className="group inline-flex w-fit items-center gap-3 rounded-full bg-brand px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-brand-dark"
              >
                Enquire About Varachha
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                data-testid="varachha-call-btn"
                href="tel:+917574942000"
                className="inline-flex w-fit items-center gap-3 rounded-full border border-navy/20 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-navy transition-colors hover:border-navy hover:bg-navy hover:text-white"
              >
                <Phone className="h-4 w-4" /> Call Varachha
              </a>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <ImageReveal src="/oneiros/varachha-outer.png" alt="Oneiros Varachha club exterior" className="aspect-[4/3] w-full" />
          </div>
        </div>
      </section>

      {/* LAUNCH OFFER */}
      <section data-testid="varachha-launch-offer" className="grain bg-navy py-20 text-white lg:py-24">
        <div className="mx-auto grid max-w-[1440px] items-center gap-10 px-6 lg:grid-cols-12 lg:px-12">
          <div className="lg:col-span-8">
            <Reveal>
              <p className="eyebrow flex items-center gap-3">
                <span className="inline-block h-[2px] w-8 bg-brand" /> <Gift className="h-4 w-4 text-brand" /> Launch Offer
              </p>
            </Reveal>
            <MaskLines lines={["Once in a Blue Moon Offer.", "First 500 Members Get NO AMC."]} className="mt-5 font-display text-3xl leading-[1.1] sm:text-4xl lg:text-5xl" />
            <Reveal delay={0.2}>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/65 md:text-base">
                The club's official leaflet calls it a moment that will never happen again — the first 500 members join with zero AMC.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.25} className="lg:col-span-4">
            <Link data-testid="varachha-offer-cta" to="/contact" className="group inline-flex items-center gap-3 rounded-full bg-brand px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-brand-dark">
              Claim Launch Benefit
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* EXPERIENCE CATEGORIES — from the official leaflet */}
      <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
        <SectionHeading
          eyebrow="The Varachha Experience"
          lines={["One Destination.", "Endless Experiences."]}
          copy="As unveiled in the club's official leaflet — a place for every mood. Workout. Play. Relax. Connect. Celebrate. All under one roof."
        />
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {VARACHHA_EXPERIENCE.map((cat, i) => (
            <Reveal key={cat.title} delay={i * 0.07}>
              <div data-testid={`varachha-experience-${i}`} className="flex h-full flex-col rounded-xl border border-hairline bg-white p-8 transition-colors hover:border-brand/50 md:p-10">
                <div className="flex items-baseline justify-between">
                  <h3 className="font-display text-3xl text-navy">{cat.title}</h3>
                  <span className="font-display text-xl text-faint">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.2em] text-brand">{cat.tagline}</p>
                <ul className="mt-6 flex flex-wrap gap-2">
                  {cat.items.map((item) => (
                    <li key={item} className="bg-mist px-3.5 py-2 text-xs font-medium text-navy/80">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* MEMBERSHIPS AT VARACHHA */}
      <section className="border-y border-hairline bg-mist py-24 lg:py-32">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
          <SectionHeading eyebrow="Memberships" lines={["Blue or Gold.", "Your Keys to the Club."]} />
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {[MEMBERSHIPS.blue, MEMBERSHIPS.gold].map((m, i) => (
              <Reveal key={m.name} delay={i * 0.08}>
                <div data-testid={`varachha-membership-${i}`} className={`flex h-full flex-col gap-5 p-8 md:p-10 ${i === 1 ? "bg-navy text-white" : "border border-hairline bg-white"}`}>
                  <p className={`eyebrow ${i === 1 ? "!text-brand" : ""}`}>{m.tagline}</p>
                  <h3 className="font-display text-4xl">{m.name}</h3>
                  <ul className="flex flex-col gap-2.5">
                    {m.benefits.map((b) => (
                      <li key={b} className={`flex items-start gap-3 text-sm ${i === 1 ? "text-white/80" : "text-body"}`}>
                        <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand" /> {b}
                      </li>
                    ))}
                  </ul>
                  <Link
                    data-testid={`varachha-membership-cta-${i}`}
                    to={i === 1 ? "/contact" : "/blue-membership"}
                    className={`mt-auto inline-flex w-fit items-center gap-2 rounded-full px-7 py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] transition-colors ${
                      i === 1 ? "bg-brand text-white hover:bg-brand-dark" : "bg-navy text-white hover:bg-brand"
                    }`}
                  >
                    {i === 1 ? "Enquire — Gold" : "Explore Blue Membership"}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY VARACHHA */}
      <section className="grain bg-navy-obsidian py-24 text-white lg:py-32">
        <div className="mx-auto grid max-w-[1440px] items-center gap-14 px-6 lg:grid-cols-2 lg:px-12">
          <div>
            <SectionHeading
              eyebrow="Why Varachha?"
              lines={["Something Surat", "Has Been Missing."]}
              dark
              copy="Designed for every generation — kids growing active and strong, adults training with purpose, and seniors staying independent and well. One place. Full family."
            />
          </div>
          <div className="grid gap-5">
            {[
              ["Kids", "Active & Fun"],
              ["Adults", "Stronger & Healthier"],
              ["Seniors", "Active & Independent"],
            ].map(([who, promise], i) => (
              <Reveal key={who} delay={i * 0.08}>
                <div className="flex items-center justify-between border border-white/12 bg-white/[0.03] px-7 py-6">
                  <span className="font-display text-2xl">{who}</span>
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand">{promise}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT STRIP */}
      <section className="mx-auto max-w-[1440px] px-6 py-20 lg:px-12">
        <div className="grid gap-6 text-sm md:grid-cols-3">
          <p className="flex items-start gap-3 text-body">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
            <span className="italic text-mute">Varachha, Surat — exact location details updating soon</span>
          </p>
          <p className="flex items-start gap-3 text-body">
            <Phone className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
            <span className="flex flex-col">
              <a data-testid="varachha-phone-1" href="tel:+917574942000" className="hover:text-brand">+91 75749 42000</a>
              <a data-testid="varachha-phone-2" href="tel:+917574943000" className="text-mute hover:text-brand">+91 75749 43000</a>
            </span>
          </p>
          <p className="flex items-start gap-3 text-body">
            <Mail className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
            <a data-testid="varachha-email" href="mailto:varachha@cluboneiros.com" className="hover:text-brand">varachha@cluboneiros.com</a>
          </p>
        </div>
      </section>

      {/* CROSS-LINKS */}
      <section className="border-t border-hairline bg-mist py-20">
        <div className="mx-auto grid max-w-[1440px] gap-6 px-6 lg:grid-cols-3 lg:px-12">
          {CLUBS.filter((c) => c.id !== "varachha").map((c, i) => (
            <Reveal key={c.id} delay={i * 0.08}>
              <Link data-testid={`varachha-crosslink-${c.id}`} to={`/clubs/${c.id}`} className="group block overflow-hidden">
                <img src={c.image} alt={`Oneiros ${c.shortName}`} loading="lazy" className="aspect-[16/9] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="flex items-center justify-between border border-hairline border-t-0 bg-white p-5">
                  <span className="font-display text-xl text-navy">{c.name}</span>
                  <ArrowRight className="h-4 w-4 text-brand transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <CTABanner
        eyebrow="Oneiros Varachha"
        lines={["Welcome to the Next", "Chapter of Oneiros."]}
        primary={{ label: "Enquire Now", to: "/contact" }}
        secondary={{ label: "Explore Blue Membership", to: "/blue-membership" }}
        testId="varachha-final-cta"
      />
    </>
  );
}
